import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IconFault } from 'svgIcons/MotionPortalIcons';
import './ValidationErrorBanner.scss';

/**
 * ValidationErrorBanner - Displays validation errors for physical connection
 * Shows at the top of the Physical Connection section when validation fails
 * Uses consistent red error style with IconFault for all validation messages
 */
const ValidationErrorBanner = ({
    validationResult,
    className = ''
}) => {
    const { t: translate } = useTranslation();

    if (!validationResult || !validationResult?.hasErrors && !validationResult?.hasWarnings && validationResult?.globalErrors?.length === 0) {
        return null;
    }

    const { globalErrors, hasErrors } = validationResult;

    // Get primary message from global errors
    const primaryMessage = (() => {
        if (globalErrors?.length > 0) {
            // Return the first error/warning message found
            return globalErrors[0].message;
        }
        // Fallback to default error message
        return translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_ValidationError_DefaultMessage');
    })();

    // Determine severity for styling
    const severity = hasErrors ? 'error' : 'warning';

    return (
        <div className={`validation-error-banner ${severity} ${className}`}>
            <div className='banner-content'>
                <div className='banner-icon'>
                    <IconFault width={20} height={20} />
                </div>
                <div className='banner-message'>
                    {primaryMessage}
                </div>
            </div>
        </div>
    );
};

ValidationErrorBanner.propTypes = {
    validationResult: PropTypes.shape({
        isValid: PropTypes.bool.isRequired,
        hasErrors: PropTypes.bool.isRequired,
        hasWarnings: PropTypes.bool.isRequired,
        totalErrors: PropTypes.number.isRequired,
        totalWarnings: PropTypes.number.isRequired,
        globalErrors: PropTypes.arrayOf(PropTypes.shape({
            type: PropTypes.string.isRequired,
            message: PropTypes.string.isRequired,
            severity: PropTypes.oneOf(['error', 'warning']).isRequired
        })).isRequired,
        assetValidations: PropTypes.array.isRequired
    }),
    className: PropTypes.string
};

export default ValidationErrorBanner;
